import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, ConfirmationComponent, CustomField, CustomFieldService, Pager, TypeFieldEnum } from 'lib-trend-core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { CustomFieldEditComponent } from './custom-field-edit/custom-field-edit.component';

@Component({
    selector: 'custom-field-component',
    // templateUrl: 'custom-field.component.html',
    templateUrl: 'custom-field.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomFieldComponent extends AbstractComponent implements OnInit {
  private selectedStatus: TypeFieldEnum | null = null;

  pager: Pager<CustomField> = new Pager<CustomField>({ perPage: 10 });
  listObservable: Observable<Pager<CustomField>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  listTypeField: Array<TypeFieldEnum> = new Array<TypeFieldEnum>(TypeFieldEnum.TEXT, TypeFieldEnum.NUMBER, TypeFieldEnum.FREETEXT, TypeFieldEnum.DATE);
  labelType: string = this.t('customField.list.filterTypes');

  constructor(
    injector: Injector,
    private dialog: MatDialog,
    private fieldsCustomService: CustomFieldService,
  ) {
    super(injector);
    this.setupForm()
    this.cacheKey = CacheKeyEnum.CUSTOM_FILDS_LIST;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<CustomField>();
    if (!cached) {
      this.getList();
      return;
    }
    this.pager = cached.pager;
    this.searchString = cached.search ?? '';
    this.loadingContent = false;

    if (this.searchString) {
      this.formGroup.get('search').setValue(this.searchString);
    }

    this.setupObservableSearch();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      search: [''],
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  setupObservableSearch() {
    this.listObservable = this.termOfSearch
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.fieldsCustomService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      }));
    this.listObservable.subscribe((pager: Pager<CustomField>) => {
      this.pager = pager;
      this.persistListState(this.pager, this.searchString, this.searchParams);
    });
  }

  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  private getList(type?: TypeFieldEnum) {
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    if (type) {
      this.searchParams['type'] = type;
    }

    this.fieldsCustomService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
      ).subscribe((pager: Pager<CustomField>) => {
        this.pager = pager;
        this.setupObservableSearch();
        this.persistListState(this.pager, this.searchString, this.searchParams);
      });
  }

  add(): void {
    const dialogRef = this.dialog.open(CustomFieldEditComponent, {
      width: '700px',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  delete(item: CustomField): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.fieldsCustomService.delete(item._id).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(this.t('customField.list.deleteSuccess'));
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  searchByType(type: TypeFieldEnum): void {
    if (type) {
      this.labelType = type === TypeFieldEnum.TEXT ? this.t('customField.list.typeText') :
        type === TypeFieldEnum.NUMBER ? this.t('customField.list.typeNumber') : type === TypeFieldEnum.FREETEXT ?
          this.t('customField.list.typeFreeText') : type === TypeFieldEnum.DATE ? this.t('customField.list.typeDate') : '-';
      this.selectedStatus = type;
    } else {
      this.labelType = this.t('customField.list.filterTypes');
      this.selectedStatus = null;
    }

    this.getList(this.selectedStatus);
  }
}
